'use client';

import { c as _c } from "react/compiler-runtime";
import { useAccess } from '@repo/auth';
import { permissions } from '@repo/permissions';
import { Button, toast } from '@repo/ui/components';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@repo/ui/components/ui/collapsible';
import { SidebarGroup, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from '@repo/ui/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components/ui/tooltip';
import { CSS, useSortable } from '@repo/ui/dnd';
import { ChevronRight, Container, GripVertical, LayoutList, Plus } from '@repo/ui/icons';
import { DynamicIcon } from '@repo/ui/icons/dynamic';
import { cn } from '@repo/ui/lib/utils';
import { useSavedViewsPanel, useTableConfiguration, useTableConfigurationSnapshot } from '@repo/table/table-configuration';
import { type ReactNode, useState } from 'react';
import { isIconName, productError, SAVED_VIEWS_TRIGGER } from '@context/saved-views';
import { PrefetchLink } from '@/app/@protected/_components/prefetch-link.tsx';
import { SortableList } from '@/app/@protected/_components/sortable-list.tsx';
import { savedViewPath } from '@/app/@protected/loads/_utils/loads-paths.ts';
import { usePrefetchLoadsList } from '@/app/@protected/loads/(table)/_hooks/use-prefetch-loads-list.ts';
import { useLoadCreationAccess } from '@/app/@protected/loads/_utils/use-load-creation-access.ts';
export const LoadsSidebarGroup = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      match: "any",
      permissions: [permissions.loads.list]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    allowed: canListLoads
  } = useAccess(t0);
  if (!canListLoads) {
    return null;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableConfigurationNavigation data-sentry-element="TableConfigurationNavigation" data-sentry-component="LoadsSidebarGroup" data-sentry-source-file="loads-sidebar-group.tsx" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
type SidebarSavedView = {
  description: null | string | undefined;
  href: string;
  icon: null | string | undefined;
  id: number;
  isActive: boolean;
  name: string;
};

/**
 * Visible pins only, in the user's order, plus Browse views. The first load shows skeleton rows;
 * failure stays quiet.
 */
const TableConfigurationNavigation = () => {
  const $ = _c(26);
  const controller = useTableConfiguration();
  const panel = useSavedViewsPanel();
  const {
    setOpenMobile
  } = useSidebar();
  const {
    active,
    pinned,
    view
  } = useTableConfigurationSnapshot();
  const panelOpen = panel.panel !== null;
  const [pinsSettled, setPinsSettled] = useState(false);
  if (!pinsSettled && (pinned.status === "ready" || pinned.status === "error")) {
    setPinsSettled(true);
  }
  let t0;
  if ($[0] !== controller || $[1] !== panel || $[2] !== panelOpen || $[3] !== setOpenMobile) {
    t0 = () => {
      if (panelOpen) {
        return panel.close();
      }
      setOpenMobile(false);
      panel.open();
      controller.refresh();
    };
    $[0] = controller;
    $[1] = panel;
    $[2] = panelOpen;
    $[3] = setOpenMobile;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <button type="button" {...{
      [SAVED_VIEWS_TRIGGER]: ""
    }} />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <LayoutList className="size-4" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== panelOpen || $[8] !== t0) {
    t3 = <SidebarMenuButton aria-expanded={panelOpen} aria-label="Browse views" className="size-8 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" onClick={t0} render={t1} tooltip="Browse views">{t2}</SidebarMenuButton>;
    $[7] = panelOpen;
    $[8] = t0;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = active && view === null;
  const t5 = !pinsSettled;
  let t6;
  if ($[10] !== controller) {
    t6 = ids => {
      controller.reorderPins(ids).catch(_temp);
    };
    $[10] = controller;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== active || $[13] !== pinned.items || $[14] !== view?.id) {
    let t8;
    if ($[16] !== active || $[17] !== view?.id) {
      t8 = item_0 => ({
        description: item_0.description,
        href: savedViewPath(item_0.id),
        icon: item_0.icon,
        id: item_0.id,
        isActive: active && view?.id === item_0.id,
        name: item_0.name
      });
      $[16] = active;
      $[17] = view?.id;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = pinned.items.filter(_temp2).map(t8);
    $[12] = active;
    $[13] = pinned.items;
    $[14] = view?.id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[19] !== active || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <LoadsNavigationGroup browse={t3} isLoadsActive={t4} isOnLoads={active} loading={t5} onReorder={t6} views={t7} data-sentry-element="LoadsNavigationGroup" data-sentry-component="TableConfigurationNavigation" data-sentry-source-file="loads-sidebar-group.tsx" />;
    $[19] = active;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
const LoadsNavigationGroup = t0 => {
  const $ = _c(13);
  const {
    browse,
    isLoadsActive,
    isOnLoads,
    loading: t1,
    onReorder,
    views: t2
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const views = t2 === undefined ? [] : t2;
  const prefetchLoads = usePrefetchLoadsList();
  const {
    canCreateNormalLoad,
    canOpenNewPage
  } = useLoadCreationAccess();
  const [savedViewsOpen, setSavedViewsOpen] = useState(isOnLoads);
  const [wasOnLoads, setWasOnLoads] = useState(isOnLoads);
  if (isOnLoads !== wasOnLoads) {
    setWasOnLoads(isOnLoads);
    if (isOnLoads) {
      setSavedViewsOpen(true);
    }
  }
  let t3;
  if ($[0] !== isLoadsActive || $[1] !== prefetchLoads) {
    t3 = <LoadsLink isActive={isLoadsActive} onMouseEnter={prefetchLoads} />;
    $[0] = isLoadsActive;
    $[1] = prefetchLoads;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const loadsLink = t3;
  const reorderable = !loading && views.length > 1;
  const T0 = SidebarGroup;
  const t4 = "SidebarGroup";
  const t5 = "LoadsNavigationGroup";
  const t6 = "loads-sidebar-group.tsx";
  const t7 = canCreateNormalLoad ? "Add order" : "New draft";
  let t8;
  if ($[3] !== canOpenNewPage || $[4] !== t7) {
    t8 = <SidebarGroupLabel className="justify-between" data-sentry-element="SidebarGroupLabel" data-sentry-source-file="loads-sidebar-group.tsx">Orders<AddOrderAction canAddLoad={canOpenNewPage} label={t7} data-sentry-element="AddOrderAction" data-sentry-source-file="loads-sidebar-group.tsx" /></SidebarGroupLabel>;
    $[3] = canOpenNewPage;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const T1 = SidebarMenu;
  const t9 = "SidebarMenu";
  const t10 = "loads-sidebar-group.tsx";
  const t11 = views.length === 0 && !loading ? <SidebarMenuItem className="flex items-center">{loadsLink}{browse}</SidebarMenuItem> : <Collapsible onOpenChange={setSavedViewsOpen} open={savedViewsOpen} render={<SidebarMenuItem className="group/loads" />}><div className="flex items-center">{loadsLink}{browse}<CollapsibleTrigger aria-label="Toggle Saved Views" render={<SidebarMenuButton className="size-8 shrink-0 group-data-[collapsible=icon]:hidden" tooltip="Toggle Saved Views" />}><ChevronRight className="size-4 transition-transform group-data-open/loads:rotate-90" /></CollapsibleTrigger></div><CollapsibleContent><SidebarMenuSub>{loading && views.length === 0 ? <SidebarMenuSubItem aria-label="Checking your pinned views" role="status"><SidebarMenuSkeleton className="h-7 px-1" showIcon={true} /><SidebarMenuSkeleton className="h-7 px-1" showIcon={true} /></SidebarMenuSubItem> : null}<SortableList disabled={!reorderable} ids={views.map(_temp3)} labels={Object.fromEntries(views.map(_temp4))} onOrderChange={onReorder}>{views.map(view_1 => <SavedViewItem key={view_1.id} reorderable={reorderable} view={view_1} />)}</SortableList></SidebarMenuSub></CollapsibleContent></Collapsible>;
  let t12;
  if ($[6] !== T1 || $[7] !== t11) {
    t12 = <T1 data-sentry-element={t9} data-sentry-source-file={t10}>{t11}</T1>;
    $[6] = T1;
    $[7] = t11;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[9] !== T0 || $[10] !== t12 || $[11] !== t8) {
    t13 = <T0 data-sentry-element={t4} data-sentry-component={t5} data-sentry-source-file={t6}>{t8}{t12}</T0>;
    $[9] = T0;
    $[10] = t12;
    $[11] = t8;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  return t13;
};

/** A pinned view's link; the grip at the row's end drags it into a new place. */
const SavedViewItem = t0 => {
  const $ = _c(43);
  const {
    reorderable,
    view
  } = t0;
  const t1 = !reorderable;
  let t2;
  if ($[0] !== t1 || $[1] !== view.id) {
    t2 = {
      disabled: t1,
      id: view.id
    };
    $[0] = t1;
    $[1] = view.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition
  } = useSortable(t2);
  const icon = view.icon && isIconName(view.icon) ? view.icon : null;
  const t3 = isDragging && "z-10 rounded-md bg-sidebar shadow-md";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("group/row", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== transform) {
    t5 = CSS.Translate.toString(transform);
    $[5] = transform;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5 || $[8] !== transition) {
    t6 = {
      transform: t5,
      transition
    };
    $[7] = t5;
    $[8] = transition;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = reorderable && "pr-7";
  let t8;
  if ($[10] !== t7) {
    t8 = cn(t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== view.href) {
    t9 = <PrefetchLink href={view.href} />;
    $[12] = view.href;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9 || $[16] !== view.isActive) {
    t10 = <SidebarMenuSubButton className={t8} isActive={view.isActive} render={t9} />;
    $[14] = t8;
    $[15] = t9;
    $[16] = view.isActive;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== icon) {
    t11 = icon ? <DynamicIcon className="size-4" name={icon} /> : null;
    $[18] = icon;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== view.name) {
    t12 = <span>{view.name}</span>;
    $[20] = view.name;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t12) {
    t13 = <TooltipTrigger render={t10} data-sentry-element="TooltipTrigger" data-sentry-source-file="loads-sidebar-group.tsx">{t11}{t12}</TooltipTrigger>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== view.description) {
    t14 = view.description ? <TooltipContent>{view.description}</TooltipContent> : null;
    $[26] = view.description;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="loads-sidebar-group.tsx">{t13}{t14}</Tooltip>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== attributes || $[32] !== listeners || $[33] !== reorderable || $[34] !== setActivatorNodeRef || $[35] !== view.name) {
    t16 = reorderable ? <Button aria-label={`Reorder ${view.name}`} className="absolute top-0.5 right-0.5 cursor-grab touch-none text-muted-foreground opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 active:cursor-grabbing pointer-coarse:opacity-100" ref={setActivatorNodeRef} size="icon-xs" variant="ghost" {...attributes} {...listeners}><GripVertical /></Button> : null;
    $[31] = attributes;
    $[32] = listeners;
    $[33] = reorderable;
    $[34] = setActivatorNodeRef;
    $[35] = view.name;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== setNodeRef || $[38] !== t15 || $[39] !== t16 || $[40] !== t4 || $[41] !== t6) {
    t17 = <SidebarMenuSubItem className={t4} ref={setNodeRef} style={t6} data-sentry-element="SidebarMenuSubItem" data-sentry-component="SavedViewItem" data-sentry-source-file="loads-sidebar-group.tsx">{t15}{t16}</SidebarMenuSubItem>;
    $[37] = setNodeRef;
    $[38] = t15;
    $[39] = t16;
    $[40] = t4;
    $[41] = t6;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
};
const AddOrderAction = t0 => {
  const $ = _c(3);
  const {
    canAddLoad,
    label
  } = t0;
  let t1;
  if ($[0] !== canAddLoad || $[1] !== label) {
    t1 = canAddLoad ? <Tooltip><TooltipTrigger aria-label={label} render={<PrefetchLink className="flex items-center gap-2" href="/loads/new" />}><Plus size={16} /></TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip> : null;
    $[0] = canAddLoad;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const LoadsLink = t0 => {
  const $ = _c(7);
  const {
    isActive,
    onMouseEnter
  } = t0;
  let t1;
  if ($[0] !== onMouseEnter) {
    t1 = <PrefetchLink href="/loads" onMouseEnter={onMouseEnter} />;
    $[0] = onMouseEnter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Container data-sentry-element="Container" data-sentry-source-file="loads-sidebar-group.tsx" />;
    t3 = <span>Loads</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isActive || $[5] !== t1) {
    t4 = <SidebarMenuButton className="min-w-0 flex-1" isActive={isActive} render={t1} tooltip="Loads" data-sentry-element="SidebarMenuButton" data-sentry-component="LoadsLink" data-sentry-source-file="loads-sidebar-group.tsx">{t2}{t3}</SidebarMenuButton>;
    $[4] = isActive;
    $[5] = t1;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
function _temp(failure) {
  return toast.error(productError(failure));
}
function _temp2(item) {
  return item.available;
}
function _temp3(view) {
  return view.id;
}
function _temp4(view_0) {
  return [view_0.id, view_0.name];
}